import React, { useMemo, useState } from "react";

import { Checkbox } from "../../../../components/ui/checkbox";
import { Input } from "../../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Textarea } from "../../../../components/ui/textarea";
import { WORKSPACE_TOOL_PROJECTION_STATUSES } from "../../projectionContract";
import ToolProjectionNotice from "./ToolProjectionNotice";
import { mayRender, resolveToolRenderStatus } from "./toolProjectionGuard";

const EMPTY_LIST = Object.freeze([]);


const SECTION_LABEL_CLASS = "text-[11px] font-semibold uppercase tracking-wide text-indigo-300";
const HOUSEHOLD_SUBJECT_ID = "household";
const ALL_FILTER_ID = "all";

// Reuses the established Agent Workspace editable-field visual language —
// the same border/fill/radius/text/placeholder treatment the Script tab's
// own QuestionInput uses (frontend/src/features/agent-workspace/components/
// panels/ScriptPanel.jsx: rounded-[8px] border-white/10, text-slate-300,
// placeholder:text-slate-600), plus the same focus-ring token already used
// elsewhere in Agent Workspace (the Lead inspector's CopyButton and the
// Agent Tools rail both use ring-indigo-400) — not a new Discovery-specific
// style, and not the shadcn default (border-input/ring-ring resolve to
// colors calibrated for a light theme this app never activates, which is
// why the previous pass under-contrasted against the dark Workspace glass
// surface). A background fill (bg-white/5) is added because Discovery's
// controls sit directly on the Agent Toolbar's own glass surface, unlike
// Script's inputs, which additionally sit inside two nested darker
// surfaces (the Script Block card and the working surface) that give them
// contrast Discovery doesn't otherwise have.
const FIELD_CONTROL_CLASS =
  "h-8 rounded-[8px] border border-white/10 bg-white/5 text-xs text-slate-200 shadow-none " +
  "placeholder:text-slate-600 focus-visible:outline-none focus-visible:ring-1 " +
  "focus-visible:ring-indigo-400 focus-visible:border-indigo-400/40";
const FIELD_TEXTAREA_CLASS = FIELD_CONTROL_CLASS.replace("h-8", "min-h-[52px]");
const CHECKBOX_CLASS =
  "border-white/20 data-[state=checked]:border-indigo-400 data-[state=checked]:bg-indigo-500 " +
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-400";

function valuesToMap(values) {
  const map = new Map();
  values.forEach((entry) => {
    map.set(`${entry.field_id}::${entry.subject_id}`, entry.value);
  });
  return map;
}

// Sanitized DOM id (no "::") shared by a field's <label htmlFor> and its
// control's `id`, so each subject's instance of a field gets its own
// accessible-name pairing even when the same field renders more than once
// (e.g. once per prospect in the "All" view).
function controlId(field, subjectId) {
  return `discovery-field-${field.id}--${subjectId}`;
}

// Subject filter offering: "All", then Household, then every prospect
// subject in sort_order — mirrors the concept of Script Runner's own
// household/prospect/all filter (WORKSPACE-AUDIT-003 §6/§10/§15) without
// importing it. Computed from the live `subjects` prop rather than assumed
// to be pre-sorted, so an out-of-order fixture still renders correctly.
function buildFilterOptions(subjects) {
  const household = subjects.find((s) => s.kind === "household");
  const prospects = subjects
    .filter((s) => s.kind === "prospect")
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const options = [{ id: ALL_FILTER_ID, name: "All" }];
  if (household) options.push(household);
  options.push(...prospects);
  return options;
}

/**
 * Renders the editable control appropriate to a field's field_type, using
 * only existing MissionAssist UI primitives (Input, Textarea, Checkbox,
 * Select) — no parallel form-control system. An unanswered value
 * (undefined/null/"") renders as a genuinely empty control, never the
 * read-only surface's "—" placeholder text, since that would be a literal
 * string inside an editable value.
 */
function DiscoveryFieldControl({ id, field, value, onChange }) {
  const stringValue = value ?? "";

  switch (field.field_type) {
    case "checkbox":
      return (
        <Checkbox
          id={id}
          className={CHECKBOX_CLASS}
          checked={value === "true"}
          onCheckedChange={(checked) => onChange(checked ? "true" : "false")}
        />
      );
    case "dropdown":
      return (
        <Select value={stringValue} onValueChange={onChange}>
          <SelectTrigger id={id} className={FIELD_CONTROL_CLASS}>
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case "textarea":
      return (
        <Textarea
          id={id}
          className={FIELD_TEXTAREA_CLASS}
          value={stringValue}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case "number":
      return (
        <Input
          id={id}
          type="number"
          className={FIELD_CONTROL_CLASS}
          value={stringValue}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case "phone":
      return (
        <Input
          id={id}
          type="tel"
          className={FIELD_CONTROL_CLASS}
          value={stringValue}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case "email":
      return (
        <Input
          id={id}
          type="email"
          className={FIELD_CONTROL_CLASS}
          value={stringValue}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    case "text":
    default:
      return (
        <Input
          id={id}
          type="text"
          className={FIELD_CONTROL_CLASS}
          value={stringValue}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
}

/**
 * A single compact, editable Discovery field row. Unlike the Lead
 * inspector's fields, an unanswered Discovery field is NOT hidden — the
 * field was configured on the active script and its absence of an answer
 * is itself meaningful, so it always renders (as an empty control) rather
 * than being omitted. Read-only in one respect only: there is no per-field
 * subject selector here — see WORKSPACE-AUDIT-003 §13/§22 for why Script
 * Runner's ephemeral per-field subject-reassignment behavior is
 * deliberately not reproduced. Which subject's value this row edits is
 * determined solely by the panel's own Show selector, above.
 */
function DiscoveryField({ field, subjectId, value, onChange }) {
  const id = controlId(field, subjectId);
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs text-slate-500">
        {field.label}
        {field.required ? (
          <span className="ml-0.5 text-rose-400" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <DiscoveryFieldControl id={id} field={field} value={value} onChange={onChange} />
    </div>
  );
}

function DiscoverySection({ title, testId, children }) {
  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      {title ? <p className={SECTION_LABEL_CLASS}>{title}</p> : null}
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

/**
 * Compact Agent Toolbar Discovery panel — a live-editing surface for
 * information collected/confirmed DURING the active Script Run, distinct
 * from the separate read-only Lead tool (information already on the lead
 * record). Fixture/local-state driven, deterministic, clearly-synthetic
 * values only — no fetch, persistence, or API behavior. Edits update local
 * component state immediately (no Save/Edit/Cancel workflow), modeling how
 * the future live-run integration will let an agent correct an
 * earlier-collected answer without navigating back through the Script.
 *
 * Subject-aware per WORKSPACE-AUDIT-003: the canonical relationship is
 * Field Definition + Subject -> Value, and the subject list is dynamic
 * (one household pseudo-subject plus N prospect subjects), not a fixed
 * two-section Prospect/Household split. A single Show selector, not a
 * per-field selector, controls which subject is currently being edited.
 *
 * Deliberately receives its subject/field/value data as props (defaulted
 * from the fixture) rather than only reading the fixture module internally,
 * so a later Boundary-A-authorized real selector/hook can supply
 * `subjects`, `fields`, and `values` in the exact same shape without
 * redesigning this presentation — see WORKSPACE-AUDIT-002 §21 (future
 * integration seam). The `values` prop is never mutated directly; it only
 * seeds this component's own local editable state.
 */
export default function DiscoveryPanel({
  subjects,
  fields,
  values,
  projectionStatus,
}) {
  // AW-ORIGINAL-RESTORE-001. Required, fail-closed. Hooks below are
  // seeded from safe empties because they run before the guard returns.
  const renderStatus = resolveToolRenderStatus(projectionStatus, [subjects, fields, values]);
  const renderable = mayRender(renderStatus);
  const safeSubjects = renderable ? subjects : EMPTY_LIST;
  const safeFields = renderable ? fields : EMPTY_LIST;

  const [selectedSubjectId, setSelectedSubjectId] = useState(HOUSEHOLD_SUBJECT_ID);
  const [valuesByKey, setValuesByKey] = useState(() =>
    renderable ? valuesToMap(values) : new Map()
  );

  const handleFieldChange = (fieldId, subjectId, nextValue) => {
    setValuesByKey((prev) => {
      const next = new Map(prev);
      next.set(`${fieldId}::${subjectId}`, nextValue);
      return next;
    });
  };

  const orderedFields = useMemo(
    () => [...safeFields].sort((a, b) => a.sort_order - b.sort_order),
    [safeFields]
  );

  const householdFields = useMemo(
    () => orderedFields.filter((field) => field.scope !== "prospects"),
    [orderedFields]
  );
  const prospectFields = useMemo(
    () => orderedFields.filter((field) => field.scope === "prospects"),
    [orderedFields]
  );

  const orderedProspectSubjects = useMemo(
    () =>
      safeSubjects
        .filter((s) => s.kind === "prospect")
        .slice()
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [safeSubjects]
  );

  const filterOptions = useMemo(() => buildFilterOptions(safeSubjects), [safeSubjects]);

  if (!renderable) {
    return (
      <ToolProjectionNotice
        status={renderStatus.status}
        reason={renderStatus.reason}
        testIdPrefix="workspace-discovery"
      />
    );
  }

  if (orderedFields.length === 0) {
    return (
      <p className="text-xs text-slate-500" data-testid="workspace-discovery-empty">
        No discovery fields for this script.
      </p>
    );
  }

  function renderFieldsForSubject(fieldList, subjectId) {
    return fieldList.map((field) => (
      <DiscoveryField
        key={field.id}
        field={field}
        subjectId={subjectId}
        value={valuesByKey.get(`${field.id}::${subjectId}`)}
        onChange={(nextValue) => handleFieldChange(field.id, subjectId, nextValue)}
      />
    ));
  }

  let content;
  if (selectedSubjectId === ALL_FILTER_ID) {
    // "All": every household field once, then every prospect-scoped field
    // once per current prospect subject — grouped so the reader always
    // knows whose values they're editing. This grouping is specific to the
    // All view; the single-subject views below are flat, ungrouped lists
    // (WORKSPACE-AUDIT-003 §8).
    content = (
      <div className="flex flex-col gap-4" data-testid="workspace-discovery-all">
        {householdFields.length ? (
          <DiscoverySection title="Household" testId="workspace-discovery-household">
            {renderFieldsForSubject(householdFields, HOUSEHOLD_SUBJECT_ID)}
          </DiscoverySection>
        ) : null}
        {prospectFields.length
          ? orderedProspectSubjects.map((subject) => (
              <DiscoverySection
                key={subject.id}
                title={subject.name}
                testId={`workspace-discovery-prospect-${subject.id}`}
              >
                {renderFieldsForSubject(prospectFields, subject.id)}
              </DiscoverySection>
            ))
          : null}
      </div>
    );
  } else if (selectedSubjectId === HOUSEHOLD_SUBJECT_ID) {
    content = (
      <DiscoverySection testId="workspace-discovery-fields">
        {renderFieldsForSubject(householdFields, HOUSEHOLD_SUBJECT_ID)}
      </DiscoverySection>
    );
  } else {
    content = (
      <DiscoverySection testId="workspace-discovery-fields">
        {renderFieldsForSubject(prospectFields, selectedSubjectId)}
      </DiscoverySection>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="workspace-discovery-panel">
      <div className="flex items-center gap-2">
        <span className="shrink-0 text-[11px] font-medium text-slate-500">Show</span>
        <Select value={selectedSubjectId} onValueChange={setSelectedSubjectId}>
          <SelectTrigger
            className={`flex-1 ${FIELD_CONTROL_CLASS}`}
            data-testid="workspace-discovery-subject-filter"
            aria-label="Show Discovery for"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {filterOptions.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {content}
    </div>
  );
}
